/**
 * The Table mode's inline edits and keyboard (Phase C2), as plain functions: what a key does,
 * what an edit box starts with, how typed text becomes a request, and how a saved answer from the
 * server is laid over the cached row. The component (InventoryTable) only wires these up.
 *
 * Money: the price box is typed in the viewer's display currency, exactly like the product
 * drawer's manual price — sent as the typed amount (`displayCents` + `displayCurrency`, which the
 * engine lists verbatim when it is the store's currency) plus the USD `manualPriceCents` the route
 * stores. What the cell then shows is the server's `listedPrice`, never a browser conversion.
 */

import type { QueryClient } from '@tanstack/react-query'
import type { Currency } from '../../../contexts/CurrencyContext'
import type { InventoryListQueryData } from './optimisticInventoryCache'
import type { InventoryProduct, ListedPrice } from './inventoryTypes'

export type EditableField = 'price' | 'quantity'

/** What one cell is doing: being saved (with what was typed), or refused (with why). */
export type CellStatus =
  | { field: EditableField; state: 'saving'; text: string }
  | { field: EditableField; state: 'error'; message: string }

export type TableKeyAction = 'up' | 'down' | 'home' | 'end' | 'pageUp' | 'pageDown' | 'open' | 'toggle' | 'editPrice' | 'editQuantity'

/** The grid's keys (only while the grid itself has focus — an edit box keeps its own keys). */
export function tableKeyAction(event: { key: string; ctrlKey?: boolean; metaKey?: boolean; altKey?: boolean }): TableKeyAction | null {
  if (event.ctrlKey || event.metaKey || event.altKey) return null
  switch (event.key) {
    case 'ArrowDown': return 'down'
    case 'ArrowUp': return 'up'
    case 'Home': return 'home'
    case 'End': return 'end'
    case 'PageDown': return 'pageDown'
    case 'PageUp': return 'pageUp'
    case 'Enter': return 'open'
    case ' ': return 'toggle'
    case 'e':
    case 'E': return 'editPrice'
    case 'q':
    case 'Q': return 'editQuantity'
    default: return null
  }
}

/** The active row after a move, clamped to the rows on the page. */
export function moveActiveIndex(current: number, action: TableKeyAction, count: number, pageSize = 10): number {
  if (count <= 0) return -1
  const from = current < 0 ? -1 : current
  switch (action) {
    case 'down': return Math.min(count - 1, from + 1)
    case 'up': return Math.max(0, from - 1)
    case 'home': return 0
    case 'end': return count - 1
    case 'pageDown': return Math.min(count - 1, Math.max(0, from) + pageSize)
    case 'pageUp': return Math.max(0, from - pageSize)
    default: return Math.min(Math.max(from, 0), count - 1)
  }
}

/** `/` focuses the search box — unless the user is typing somewhere already. */
export function isSearchShortcut(event: { key: string; ctrlKey?: boolean; metaKey?: boolean; altKey?: boolean; target: EventTarget | null }): boolean {
  if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) return false
  const target = event.target as HTMLElement | null
  if (!target || typeof target.tagName !== 'string') return true
  const tag = target.tagName.toLowerCase()
  return !(tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable)
}

/** "12.5", "$12.50", "1,299.00" → cents; null for anything that isn't a positive amount up to 1,000,000. */
export function parseMoneyInput(text: string): number | null {
  const cleaned = text.trim().replace(/^[^\d.-]+/, '').replace(/,/g, '')
  if (!/^\d+(\.\d{0,2})?$|^\.\d{1,2}$/.test(cleaned)) return null
  const cents = Math.round(Number(cleaned) * 100)
  return Number.isFinite(cents) && cents > 0 && cents <= 100_000_000 ? cents : null
}

/** A whole stock count, 0 to 100,000. */
export function parseQuantityInput(text: string): number | null {
  const cleaned = text.trim()
  if (!/^\d+$/.test(cleaned)) return null
  const value = Number(cleaned)
  return value <= 100_000 ? value : null
}

/** The price box's starting text: the price the user would be overriding, when it is in their currency. */
export function priceEditInitialValue(product: InventoryProduct, viewerCurrency: string): string {
  const figures = product.storePrices
  if (!figures || figures.currency !== viewerCurrency) return ''
  const cents = figures.manualPrice?.cents ?? figures.autoPriceCents
  return cents > 0 ? (cents / 100).toFixed(2) : ''
}

/**
 * Body for PATCH /inventory/:id/price from a typed amount, or why it can't be sent. No exchange
 * rate → refuse; never guess one (the drawer's rule).
 */
export function manualPricePayload(
  displayCents: number,
  viewerCurrency: Currency,
  baseCurrency: Currency,
  tryConvertPrice: (cents: number, from: Currency, to: Currency) => number | null,
): { ok: true; payload: { manualPriceCents: number; duration: 'forever'; displayCents: number; displayCurrency: string } } | { ok: false; message: string } {
  const manualPriceCents = viewerCurrency === baseCurrency
    ? displayCents
    : tryConvertPrice(displayCents, viewerCurrency, baseCurrency)
  if (manualPriceCents === null || !Number.isFinite(manualPriceCents)) {
    return { ok: false, message: "Exchange rates are unavailable right now, so this price can't be saved." }
  }
  return { ok: true, payload: { manualPriceCents: Math.round(manualPriceCents), duration: 'forever', displayCents, displayCurrency: viewerCurrency } }
}

/** Lay `patch` over one product in every cached list page (the server's answer, or a stock delta). */
export function patchCachedProduct(queryClient: QueryClient, productId: string, patch: (product: InventoryProduct) => InventoryProduct): void {
  queryClient.setQueriesData({ queryKey: ['inventory', 'list'] }, (old: InventoryListQueryData | undefined) => {
    if (!old?.products) return old
    let changed = false
    const products = old.products.map((product) => {
      if (product.id !== productId) return product
      changed = true
      return patch(product)
    })
    return changed ? { ...old, products } : old
  })
}

/** A stock delta laid over a cached row, for the optimistic update (rolled back on error). */
export function withQuantityDelta(product: InventoryProduct, delta: number): InventoryProduct {
  return {
    ...product,
    quantity: product.quantity + delta,
    availableQuantity: product.availableQuantity + delta,
  }
}

/** The PATCH /price answer laid over the cached row: the server's listed price and manual fields. */
export function withSavedPrice(
  product: InventoryProduct,
  saved: { manualPriceCents?: number | null; manualPriceExpiresAt?: string | null; listedPrice?: ListedPrice | null },
): InventoryProduct {
  return {
    ...product,
    manualPriceCents: saved.manualPriceCents ?? null,
    manualPriceExpiresAt: saved.manualPriceExpiresAt ?? null,
    listedPrice: saved.listedPrice ?? product.listedPrice,
  }
}

/** Error text from an API failure (the axios interceptor fills `message`; the server sends `error`). */
export function apiErrorText(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { message?: string; error?: string } } })?.response?.data
  return data?.message || data?.error || fallback
}
